import { FC } from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { ComboboxMulti, FreeInputCombobox } from "@/components/elements/Combobox";
import { InputDatePicker } from "@/components/elements/InputDatePicker/InputDatePicker";
import { Input } from "@/components/elements/Input";
import { INPUT_BOX_HEIGHT } from "@/constants/ui";
import { TCaseEditMatchFilterFormExtended } from "@/features/case/components/CaseEdit/case-edit-match-filter-shared";
import { useCaseEditMatchFilterMasterData } from "@/features/case/components/CaseEdit/use-case-edit-match-filter-master-data";
import { useTagOptions } from "@/api/services/main/tag";
import { useMemo } from "react";
import { TOption } from "@/types/common";
import { dayjs } from "@/utils/dayjs";
import { Dayjs } from "dayjs";

type CaseEditMatchFilterFormFieldsProps = {
  filterValues: TCaseEditMatchFilterFormExtended;
  onFilterChange: (field: keyof TCaseEditMatchFilterFormExtended, value: unknown) => void;
  interviewDateOptions?: TOption[];
  /** Stable options from unfiltered list — not affected by active filters */
  talentNumberOptions?: TOption[];
  fullNameOptions?: TOption[];
  nickNameOptions?: TOption[];
};

/**
 * Filter form fields for case edit match talent filtering.
 * Layout: 2-column grid with 12 fields.
 */
export const CaseEditMatchFilterFormFields: FC<CaseEditMatchFilterFormFieldsProps> = ({
  filterValues,
  onFilterChange,
  interviewDateOptions = [],
  talentNumberOptions,
  fullNameOptions,
  nickNameOptions,
}) => {
  const { truncatedOptions } = useCaseEditMatchFilterMasterData();
  const { data: caseMatchTagOptions } = useTagOptions("case-match");
  const tagOptions = useMemo<TOption[]>(() => caseMatchTagOptions.map((opt) => ({ ...opt, value: String(opt.value) })), [caseMatchTagOptions]);

  return (
    <Grid container rowSpacing={2} columnSpacing={3}>
      {/* Row 1: ステータス / 人材管理番号 */}
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">ステータス</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.status}
            onChange={(value) => onFilterChange("status", value)}
            options={truncatedOptions.status}
          />
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">人材管理番号</Typography>
          <FreeInputCombobox
            placeholder="入力してください"
            value={filterValues.talentNumber}
            onChange={(value) => onFilterChange("talentNumber", value)}
            options={talentNumberOptions ?? truncatedOptions.talentNumber}
          />
        </Stack>
      </Grid>

      {/* Row 2: 面接日 (multi-select) */}
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">面接日</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.interviewDate}
            onChange={(value) => onFilterChange("interviewDate", value)}
            options={interviewDateOptions}
          />
        </Stack>
      </Grid>

      {/* Row 3: 名前 / 呼び名 */}
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">名前</Typography>
          <FreeInputCombobox
            placeholder="入力してください"
            value={filterValues.fullName}
            onChange={(value) => onFilterChange("fullName", value)}
            options={fullNameOptions ?? truncatedOptions.fullName}
          />
        </Stack>
      </Grid>

      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">呼び名</Typography>
          <FreeInputCombobox
            placeholder="入力してください"
            value={filterValues.nickName}
            onChange={(value) => onFilterChange("nickName", value)}
            options={nickNameOptions ?? truncatedOptions.nickName}
          />
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">国籍</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.nationalityId}
            onChange={(value) => onFilterChange("nationalityId", value)}
            options={truncatedOptions.nationality}
          />
        </Stack>
      </Grid>

      {/* Row 4: 性別 / 年齢 */}
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">性別</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.gender}
            onChange={(value) => onFilterChange("gender", value)}
            options={truncatedOptions.gender}
          />
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">年齢</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Input
              nonControl
              value={filterValues.ageFrom}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9]/g, "");
                onFilterChange("ageFrom", v);
              }}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <Typography variant="cap12Light">〜</Typography>
            <Input
              nonControl
              value={filterValues.ageTo}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9]/g, "");
                onFilterChange("ageTo", v);
              }}
              sx={{ flex: 1, minWidth: 0 }}
            />
          </Stack>
        </Stack>
      </Grid>

      {/* Row 5: 在留資格 */}
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">在留資格</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.residenceStatusId}
            onChange={(value) => onFilterChange("residenceStatusId", value)}
            options={truncatedOptions.residenceStatus}
          />
        </Stack>
      </Grid>

      {/* 在留期限（開始〜終了）*/}
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">在留期限</Typography>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box sx={{ flex: 1 }}>
              <InputDatePicker
                nonControl
                value={filterValues.periodOfStayFrom ? dayjs(filterValues.periodOfStayFrom) : null}
                onChange={(value: Dayjs | null) => onFilterChange("periodOfStayFrom", value ? value.format("YYYY-MM-DD") : "")}
                label="在留期限"
                maxDate={filterValues.periodOfStayTo ? dayjs(filterValues.periodOfStayTo) : undefined}
              />
            </Box>
            <Typography variant="cap12Light" sx={{ height: INPUT_BOX_HEIGHT, display: "flex", alignItems: "center" }}>〜</Typography>
            <Box sx={{ flex: 1 }}>
              <InputDatePicker
                nonControl
                value={filterValues.periodOfStayTo ? dayjs(filterValues.periodOfStayTo) : null}
                onChange={(value: Dayjs | null) => onFilterChange("periodOfStayTo", value ? value.format("YYYY-MM-DD") : "")}
                label="在留期限"
                minDate={filterValues.periodOfStayFrom ? dayjs(filterValues.periodOfStayFrom) : undefined}
              />
            </Box>
          </Stack>
        </Stack>
      </Grid>

      {/* Row 6: 日本語能力 / 担当者 */}
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">日本語能力</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.languageTestLevelId}
            onChange={(value) => onFilterChange("languageTestLevelId", value)}
            options={truncatedOptions.japaneseLevelType}
          />
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">担当者</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.personInChargeId}
            onChange={(value) => onFilterChange("personInChargeId", value)}
            options={truncatedOptions.personInCharge}
          />
        </Stack>
      </Grid>

      {/* Row 7: タグ */}
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">タグ</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.tagIds}
            onChange={(value) => onFilterChange("tagIds", value)}
            options={tagOptions}
          />
        </Stack>
      </Grid>
    </Grid>
  );
};
